import React, { useCallback, useState, useEffect, useMemo } from 'react';
import {
  FlatList,
  Text,
  Spinner,
  VStack,
  HStack,
  Divider,
  Center,
  Input,
  Button,
  Box,
  Modal,
  Select,
} from 'native-base';
import { useSelector, useDispatch } from 'react-redux';
import { useFocusEffect } from '@react-navigation/native';

import * as UploadAction from 'src/redux/actions/dataset';
import { fetchRejectionMapping, deleteDataset } from 'src/redux/actions/dataset';
import * as JobActions from 'src/redux/actions/jobAction';
import * as GrainTypeAction from 'src/redux/actions/grainTypeAction';
import { clearDataset } from 'src/redux/actions/dataset';
import { IStore } from 'src/redux/reducers';
import { getGrainName } from 'src/utils/utils';
import { useDebounce } from 'src/hooks/useDebounce';
import { parseDatasetName } from 'src/utils/datasetUtils';

import DatasetListItem from 'src/components/listItem/DatasetListItem';
import DatasetTableHeader from 'src/components/ml/DatasetTable/DatasetTableHeader';
import { getText } from 'src/utils/i18n';

const PAGE_SIZE = 10;

const DatasetListingPage = ({ navigation, route }: { navigation: any; route: any }) => {
  const dispatch = useDispatch();

  const {
    datasets,
    loading,
    nextKey,
    rejectionMapping,
    labelCountsMap,
    canDeleteDataset,
    orgJobs,
    orgId,
    grainTypes,
    filterGrainId,
    selectedGrainId
  } = useSelector((state: IStore) => ({
    datasets: state.dataset.datasets,
    loading: state.dataset.loading,
    nextKey: state.dataset.nextKey,
    rejectionMapping: state.dataset.rejectionMapping,
    labelCountsMap: state.dataset.labelCountsMap,
    canDeleteDataset: state.user.userProfile.response?.settings?.delete_ml_dataset_allowed,
    orgJobs: state.job.orgJobs.jobs,
    orgId: state.user.userProfile.response?.org?.orgId ?? '',
    grainTypes: state.user.userProfile.response?.grains,
    filterGrainId: state.app.grainIdFilter || 'all',
    selectedGrainId: state.grainType.grainId || 'all'
  }));
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [statisticsModalVisible, setStatisticsModalVisible] = useState(false);
  const [selectedDatasetForStats, setSelectedDatasetForStats] = useState<any>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedDatasetToDelete, setSelectedDatasetToDelete] = useState<string | null>(null);
  const [lastFetchedGrainId, setLastFetchedGrainId] = useState<string | null>(null);

  useFocusEffect(
    useCallback(() => {
      setStatisticsModalVisible(false);
      setSelectedDatasetForStats(null);
      dispatch(UploadAction.setUploadProgress(0));
      dispatch(UploadAction.uploadComplete(false));
    }, [dispatch])
  );

  useEffect(() => {
    if (route.params?.refresh) {
      // Clear existing datasets and reload fresh data
      dispatch(clearDataset());
      dispatch(UploadAction.fetchDatasetsByGrainId.request({ grainId: filterGrainId || 'all', limit: PAGE_SIZE, lastKey: null }));

      // Immediately clear the refresh param after handling
      navigation.setParams({ refresh: undefined });
    }
  }, [route.params?.refresh, dispatch, navigation]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsInitialLoading(false);
    }, 1500);
    return () => clearTimeout(timeoutId);
  }, []);

  useFocusEffect(
    useCallback(() => {
      dispatch(UploadAction.setIsExistingDataset(true));
    }, [dispatch])
  );

  // Separate effect to fetch orgJobs only once when orgId is available
  useEffect(() => {
    if (!orgJobs && orgId) {
      dispatch(JobActions.orgJobs.request({ orgId }));
    }
  }, [orgId, orgJobs, dispatch]);

  // Effect to fetch datasets when grainId changes
  useEffect(() => {
    const grainIdFilter = selectedGrainId || filterGrainId || 'all';
    
    // Only fetch if the grainId has actually changed
    if (grainIdFilter !== lastFetchedGrainId) {
      setLastFetchedGrainId(grainIdFilter);
      dispatch(clearDataset());
      dispatch(UploadAction.fetchDatasetsByGrainId.request({ grainId: grainIdFilter, limit: PAGE_SIZE, lastKey: null }));
    }
  }, [selectedGrainId, filterGrainId, lastFetchedGrainId, dispatch]);


  const filterBySearchTerm = useCallback((dataset: any) => {
    const lowerSearch = debouncedSearchTerm.toLowerCase();
    const datasetID = dataset.datasetID ? dataset.datasetID.toLowerCase() : '';
    const username = dataset.username ? dataset.username.toLowerCase() : '';
    const grainName = (getGrainName(grainTypes, dataset.grainId) || "").toLowerCase();
    return (
      datasetID.includes(lowerSearch) ||
      username.includes(lowerSearch) ||
      grainName.includes(lowerSearch)
    );
  }, [debouncedSearchTerm, grainTypes]);

  const filterByGrainId = useCallback((dataset: any) => {
    const grainIdFilter = selectedGrainId || filterGrainId || 'all';
    return grainIdFilter === 'all' || dataset.grainId === grainIdFilter;
  }, [filterGrainId, selectedGrainId]);

  const filterByStatus = useCallback((dataset: any) => {
    return statusFilter === 'all' || dataset.status === statusFilter;
  }, [statusFilter]);

  const sortByUpdatedAt = useCallback((a: any, b: any) => {
    const aTime = a.updatedAt || a.createdAt || 0;
    const bTime = b.updatedAt || b.createdAt || 0;
    return bTime - aTime;
  }, []);

  const filteredDatasets = useMemo(() => {
    return datasets
      .filter(filterBySearchTerm)
      .filter(filterByGrainId)
      .filter(filterByStatus)
      .sort(sortByUpdatedAt);
  }, [datasets, debouncedSearchTerm, filterGrainId, statusFilter, grainTypes, filterBySearchTerm, filterByGrainId, filterByStatus, sortByUpdatedAt]);

  const getEmptyStateMessage = useCallback(() => {
    const hasActiveFilters = debouncedSearchTerm || statusFilter !== 'all' || filterGrainId !== 'all';

    if (hasActiveFilters) {
      return getText('noDatasetFilterMatch');
    }

    return getText('noDatasetsAvailable');
  }, [debouncedSearchTerm, statusFilter, filterGrainId]);

  const getEmptyStateAction = useCallback(() => {
    const hasActiveFilters = debouncedSearchTerm || statusFilter !== 'all' || (filterGrainId !== 'all' && selectedGrainId !== 'all');

    if (hasActiveFilters) {
      return (
        <Button
          variant="outline"
          onPress={() => {
            setSearchTerm('');
            setStatusFilter('all');
          }}
        >
          Clear Filters
        </Button>
      );
    }

    // Show "Load More Datasets" when there are no active filters
    return (
      <Button
        onPress={() => {
          // If there's a nextKey use the loadMoreDatasets handler, otherwise trigger a fresh fetch
          const grainIdFilter = selectedGrainId || filterGrainId || 'all';
          if (nextKey) {
            loadMoreDatasets();
          } else {
            dispatch(clearDataset());
            dispatch(UploadAction.fetchDatasetsByGrainId.request({ grainId: grainIdFilter, limit: PAGE_SIZE, lastKey: null }));
          }
        }}
      >
        Load More Datasets
      </Button>
    );
  }, [debouncedSearchTerm, statusFilter, filterGrainId, selectedGrainId, nextKey, dispatch]);


  const loadMoreDatasets = useCallback(async () => {
    if (isFetchingMore || !nextKey) return;
    setIsFetchingMore(true);
    try {
      const grainIdFilter = selectedGrainId || filterGrainId || 'all';
      dispatch(UploadAction.fetchDatasetsByGrainId.request({ grainId: grainIdFilter, limit: PAGE_SIZE, lastKey: nextKey }));
    } catch (error) {
      console.error('Error loading more datasets:', error);
    } finally {
      setIsFetchingMore(false);
    }
  }, [dispatch, isFetchingMore, nextKey, filterGrainId, selectedGrainId]);

  const handleViewStatistics = useCallback(
    async (item: any) => {
      setSelectedDatasetForStats(item);
      setStatisticsModalVisible(true);
      dispatch(fetchRejectionMapping.request({ grainId: item.grainId }));
      dispatch(UploadAction.fetchLabelCounts.request({ datasetId: item.datasetID }));
    },
    [dispatch]
  );
  const handleLabelDataset = useCallback(
    async (datasetId: string) => {
      // Clear previous dataset state before loading new one
      dispatch(clearDataset());
      
      dispatch(UploadAction.setIsExistingDataset(true));
      const selectedDataset = datasets.find((dataset) => dataset.datasetID === datasetId);

      if (selectedDataset) {
        dispatch(GrainTypeAction.setGrainId(selectedDataset.grainId));
        let extractedJobIdentifier = datasetId;
        let isJobBasedDataset = false;

        const parsedName = parseDatasetName(datasetId);
        if (parsedName && parsedName.isJobBased && parsedName.jobId) {
          isJobBasedDataset = true;
          extractedJobIdentifier = parsedName.jobId;
        } else if (datasetId.startsWith('dataset_jobId_')) {
          isJobBasedDataset = true;
          extractedJobIdentifier = datasetId.replace('dataset_jobId_', '');
        }

        if (isJobBasedDataset) {
          const selectedJobStatus = orgJobs?.Items.find((job) => job.jobId === extractedJobIdentifier);
          if (selectedJobStatus) {
            dispatch(JobActions.jobStatus.success(selectedJobStatus));
            dispatch(JobActions.setJobId(datasetId));
            dispatch(JobActions.pollJobStatus.request(extractedJobIdentifier));
          }
          dispatch(UploadAction.setIsJob(true));
          dispatch(UploadAction.setId(datasetId));
          navigation.navigate('CombinedLabelingPage', { datasetId });
        } else {
          dispatch(UploadAction.setDatasetId(datasetId));
          dispatch(UploadAction.setIsJob(false));
          dispatch(UploadAction.setId(datasetId));
          navigation.navigate('CombinedLabelingPage', { datasetId });
        }
      }
    },
    [dispatch, navigation, orgJobs, datasets]
  );

  const handleUploadImages = useCallback(
    (datasetId: string) => {
      const selectedDataset = datasets.find(d => d.datasetID === datasetId);
      if (selectedDataset) {
        dispatch(GrainTypeAction.setGrainId(selectedDataset.grainId));
        dispatch(UploadAction.setIsExistingDataset(true));
        dispatch(UploadAction.setDatasetId(datasetId));
        navigation.navigate('ImageUploadPage', { datasetId });
      }
    },
    [dispatch, navigation, datasets]
  );

  const handleDeleteDataset = useCallback(
    (datasetId: string) => {
      setSelectedDatasetToDelete(datasetId);
      setDeleteModalVisible(true);
    },
    []
  );

  // Confirm deletion: dispatch the delete action
  const confirmDelete = () => {
    if (selectedDatasetToDelete) {
      dispatch(deleteDataset.request(selectedDatasetToDelete));
    }
    setDeleteModalVisible(false);
    setSelectedDatasetToDelete(null);
  };

  // Cancel deletion: simply close the modal
  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setSelectedDatasetToDelete(null);
  };

  const renderItem = useCallback(
    ({ item, index }: { item: any; index: number }) => (
      <DatasetListItem
        item={item}
        rowNumber={index + 1}
        onDatasetPress={handleViewStatistics}
        onLabelDataset={handleLabelDataset}
        onUploadImages={handleUploadImages}
        onDeleteDataset={canDeleteDataset ? handleDeleteDataset : null}
        onViewStatistics={handleViewStatistics}
        getGrainName={(grainId) => getGrainName(grainTypes, grainId)}
      />
    ),
    [
      labelCountsMap,
      handleViewStatistics,
      handleLabelDataset,
      handleUploadImages,
      handleDeleteDataset,
      grainTypes,
      rejectionMapping,
      canDeleteDataset,
    ]
  );

  if (isInitialLoading) {
    return (
      <Center flex={1} px="3">
        <Spinner accessibilityLabel="Loading datasets" />
      </Center>
    );
  }

  return (
    <VStack space={4} p={5} flex={1} bg="coolGray.50">
      <HStack space={2} justifyContent="space-between" alignItems="center">
        <Text fontSize="xl" fontWeight="bold">
          {getText('availableDatasets')}
        </Text>
        <Button
          onPress={() => {
            const grainIdFilter = selectedGrainId || filterGrainId || 'all';
            dispatch(clearDataset());
            dispatch(UploadAction.fetchDatasetsByGrainId.request({ grainId: grainIdFilter, limit: PAGE_SIZE, lastKey: null }));
          }}
        >
          {getText('refreshDatasets')}
        </Button>
      </HStack>
      <HStack space={2} alignItems="center">
        <Input
          flex={1}
          placeholder={getText('searchByUsername')}
          value={searchTerm}
          onChangeText={setSearchTerm}
        />
        <Select
          selectedValue={statusFilter}
          minWidth="180"
          accessibilityLabel={getText('filterByStatus')}
          placeholder={getText('allStatuses')}
          onValueChange={(itemValue) => setStatusFilter(itemValue)}
        >
          <Select.Item label={getText('allStatuses')} value="all" />
          <Select.Item label={getText('statusNotLabeled')} value="NOT_LABELED" />
          <Select.Item label={getText('statusInProgress')} value="IN_PROGRESS" />
          <Select.Item label={getText('statusCompleted')} value="COMPLETED" />
        </Select>
      </HStack>

      <Divider />

      {filteredDatasets.length > 0 ? (
        <VStack space={0} flex={1}>
          <DatasetTableHeader />
          <FlatList
            data={filteredDatasets}
            renderItem={renderItem}
            keyExtractor={(item) => item.datasetID}
            showsVerticalScrollIndicator={false}
            ListFooterComponent={
              nextKey ? (
                <Center py={4}>
                  <Button onPress={loadMoreDatasets}>{getText('loadMore')}</Button>
                </Center>
              ) : null
            }
            refreshing={loading}
            onRefresh={() => {
              const grainIdFilter = selectedGrainId || filterGrainId || 'all';
              dispatch(clearDataset());
              dispatch(UploadAction.fetchDatasetsByGrainId.request({ grainId: grainIdFilter, limit: PAGE_SIZE, lastKey: null }));
            }}
          />
        </VStack>
      ) : (
        <VStack space={0} flex={1}>
          <DatasetTableHeader />
          {loading ? (
            <Center flex={1}>
              <Spinner accessibilityLabel="Loading datasets" />
            </Center>
          ) : (
            <Center flex={1} px={8}>
              <Text fontSize="lg" color="gray.500" textAlign="center" mb={4}>
                {getEmptyStateMessage()}
              </Text>
              {getEmptyStateAction()}
            </Center>
          )}
        </VStack>
      )}
      <Modal isOpen={deleteModalVisible} onClose={cancelDelete}>
        <Modal.Content>
          <Modal.CloseButton />
          <Modal.Header>{getText('confirmDelete')}</Modal.Header>
          <Modal.Body>
            <Text>
              Are you sure you want to delete {selectedDatasetToDelete}?
            </Text>
          </Modal.Body>
          <Modal.Footer>
            <Button.Group space={2}>
              <Button variant="ghost" onPress={cancelDelete}>
                {getText('cancel')}
              </Button>
              <Button colorScheme="danger" onPress={confirmDelete}>
                {getText('delete')}
              </Button>
            </Button.Group>
          </Modal.Footer>
        </Modal.Content>
      </Modal>

      {/* Statistics Modal */}
      <Modal isOpen={statisticsModalVisible} onClose={() => setStatisticsModalVisible(false)} size="lg">
        <Modal.Content>
          <Modal.CloseButton />
          <Modal.Header>{getText('datasetStatistics')}</Modal.Header>
          <Modal.Body>
            {selectedDatasetForStats && (
              <VStack space={4}>
                <HStack justifyContent="space-between">
                  <Text fontWeight="bold">{getText('datasetIdLabel')}:</Text>
                  <Text>{selectedDatasetForStats.datasetID}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontWeight="bold">{getText('grainTypeLabel')}:</Text>
                  <Text>{getGrainName(grainTypes, selectedDatasetForStats.grainId)}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontWeight="bold">{getText('statusLabel')}:</Text>
                  <Text>{selectedDatasetForStats.status}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontWeight="bold">{getText('createdByLabel')}:</Text>
                  <Text>{selectedDatasetForStats.username}</Text>
                </HStack>
                {selectedDatasetForStats && labelCountsMap[selectedDatasetForStats.datasetID] && Object.keys(labelCountsMap[selectedDatasetForStats.datasetID].counts).length > 0 && (
                  <VStack space={2}>
                    <Text fontWeight="bold" fontSize="md">{getText('labelCountsLabel')}:</Text>
                    <HStack space={3} flexWrap="wrap" justifyContent="flex-start">
                      {Object.entries(labelCountsMap[selectedDatasetForStats.datasetID].counts).map(([labelKey, count]) => {
                        const displayLabel = rejectionMapping[labelKey] || labelKey;
                        return (
                          <Box
                            key={labelKey}
                            borderWidth={1}
                            borderColor="coolGray.300"
                            borderRadius="md"
                            p={3}
                            mb={2}
                            bg="coolGray.50"
                          >
                            <Text fontSize="sm" fontWeight="medium">{displayLabel}</Text>
                            <Text fontSize="lg" textAlign="center" color="primary.600">{count}</Text>
                          </Box>
                        );
                      })}
                    </HStack>
                  </VStack>
                )}
              </VStack>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button onPress={() => setStatisticsModalVisible(false)}>
              {getText('close')}
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    </VStack>
  );
};

export default DatasetListingPage;
